import configJson from "./auth_config.json";

export const normalizeBackendUrl = (value) => {
  if (!value) return "/api";
  const trimmed = String(value).trim().replace(/\/+$/, "");
  if (/^https?:\/\//i.test(trimmed)) {
    const url = new URL(trimmed);
    let path = url.pathname || "/";
    if (path === "/") {
      path = "/api";
    } else if (!path.startsWith("/api")) {
      path = `/api${path.startsWith("/") ? path : `/${path}`}`;
    }
    const normalizedPath = path.replace(/\/+$/, "");
    // If the app is served over HTTPS, never call an explicit HTTP API URL from the browser.
    // Prefer same-origin /api so Nginx can proxy internally.
    if (
      typeof window !== "undefined" &&
      window.location?.protocol === "https:" &&
      url.protocol === "http:"
    ) {
      return normalizedPath || "/api";
    }
    url.pathname = normalizedPath;
    return url.toString();
  }

  let path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  if (path === "/") return "/api";
  if (!path.startsWith("/api")) path = `/api${path}`;
  return path || "/api";
};

const isTrue = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase() === "true";
export const IS_PRODUCTION = Boolean(import.meta.env.PROD);
export const isProductionBuild = () => IS_PRODUCTION;

const rawBackendUrl = import.meta.env.VITE_BACKEND_URL ?? "/api";

export const BACKEND_URL = normalizeBackendUrl(rawBackendUrl);

const envDomain = import.meta.env.VITE_AUTH0_DOMAIN ?? null;

const envClientId = import.meta.env.VITE_AUTH0_CLIENT_ID ?? null;

const configDomain =
  configJson.domain && !configJson.domain.startsWith("YOUR_")
    ? configJson.domain
    : null;

const configClientId =
  configJson.clientId && !configJson.clientId.startsWith("YOUR_")
    ? configJson.clientId
    : null;

const resolvedDomain = envDomain ?? configDomain ?? null;
const resolvedClientId = envClientId ?? configClientId ?? null;

const envAudience = import.meta.env.VITE_AUTH0_AUDIENCE ?? null;

const configAudience =
  configJson.audience && !configJson.audience.startsWith("YOUR_")
    ? configJson.audience
    : null;

export const AUTH0_AUDIENCE = envAudience ?? configAudience ?? null;

const envAdminRolesClaim = import.meta.env.VITE_ADMIN_ROLES_CLAIM ?? null;

export const ADMIN_ROLES_CLAIM =
  envAdminRolesClaim ?? "https://reabel.app/roles";

const envAdminRolesClaimEnforcement =
  import.meta.env.VITE_ADMIN_ROLES_CLAIM_ENFORCEMENT ?? "log_only";
export const ADMIN_ROLES_CLAIM_ENFORCEMENT =
  String(envAdminRolesClaimEnforcement).trim().toLowerCase() === "strict"
    ? "strict"
    : "log_only";

const envAdminRequiredRole = import.meta.env.VITE_ADMIN_REQUIRED_ROLE ?? null;

export const ADMIN_REQUIRED_ROLE = envAdminRequiredRole ?? "admin";

const envEnableDevLogin = import.meta.env.VITE_ENABLE_DEV_LOGIN ?? "false";
export const ENABLE_DEV_LOGIN =
  String(envEnableDevLogin).toLowerCase() !== "false";
// Primary switch for non-Auth0 login shortcuts.
export const DEV_LOGIN_ENABLED = ENABLE_DEV_LOGIN;

const envAdminLoginEnabled =
  import.meta.env.VITE_ADMIN_LOGIN_ENABLED ?? "false";
export const ADMIN_LOGIN_ENABLED =
  String(envAdminLoginEnabled).toLowerCase() === "true";

const envStudentGateEnabled =
  import.meta.env.VITE_STUDENT_GATE_ENABLED ?? "false";
export const STUDENT_GATE_ENABLED =
  String(envStudentGateEnabled).toLowerCase() === "true";

const envAuthEnabled = import.meta.env.VITE_AUTH_ENABLED;
export const AUTH_ENABLED =
  isTrue(envAuthEnabled) && Boolean(resolvedDomain && resolvedClientId);
export const AUTH_OR_DEV_LOGIN_ENABLED = AUTH_ENABLED || DEV_LOGIN_ENABLED;

const envPublicPricingUrl = import.meta.env.VITE_PUBLIC_PRICING_URL ?? "";
export const PUBLIC_PRICING_URL = String(envPublicPricingUrl || "").trim();

export function getConfig() {
  return {
    domain: resolvedDomain,
    clientId: resolvedClientId,
    ...(AUTH0_AUDIENCE ? { audience: AUTH0_AUDIENCE } : null),
  };
}
